/**
 * 端末ID⇄編成番号の紐づけ一覧ページ (SBI-5)。
 *
 * 現場 WG の WebApp 上で、DeviceRegistry テーブルに登録されている
 * 「端末ID (device_id) と編成番号 (vehicle_id) の最新紐づけ + 動作ステータス」を
 * 一覧確認する用途。
 *
 * 表示列:
 *   - 編成番号 (vehicle_id)
 *   - 端末ID (device_id)
 *   - 端末名 (device_name。任意入力。入力欄を常に表示し、設定・解除できる。Issue #360)
 *   - デバイスID (airdroid_device_id。AirDroid Business 上の端末 ID。端末名と同じ
 *     行の入力欄に運用者が転記する。再起動対象の特定に使う / C-5)
 *     端末名とデバイスID は行単位で同時に保存し、デバイスID の横の「保存」で 2 項目とも送る。
 *     項目ごとに保存すると、サーバの PUT が送られなかった項目を解除するため
 *     もう片方が消える (Issue #885)
 *   - 種別 (Web / Android。device_id プレフィックスからフロント側で導出)
 *   - 最終登録日時 (last_registered_at)
 *   - 現在の列番 (current_trip_id。VehicleTripMapping 由来。在線情報が無い場合は "—")
 *   - ステータス (正常=緑バッジ / 異常=赤バッジ + last_alert_sent_at ツールチップ)
 *   - 状態更新時刻 (status_updated_at)
 *   - 最終再起動 (last_reboot_requested_at + 結果バッジ / C-5)
 *   - 操作 (行ごとの再起動ボタン / C-5。**現在は常に無効** / S-9)
 *   - 先頭列: 一括削除用のチェックボックス (Issue #638)
 *
 * 一括削除 (Issue #638):
 *   - 行のチェックボックスで削除対象を選び、ヘッダーのチェックボックスで表示中の行を
 *     全選択 / 全解除できる。
 *   - 「選択した端末を削除」ボタンは ConfirmDialog で確認してから
 *     POST /device-registrations/bulk-delete を呼ぶ。
 *   - 削除対象は **表示中 (フィルタ後) かつチェック済み** の行のみ。「異常のみ表示」で
 *     隠れている行を巻き込んで削除する事故を防ぐ。
 *
 * フィルタ:
 *   - 「異常のみ表示」トグル (status === 'abnormal' の行のみ表示)
 *
 * ⚠️ 再起動 (C-5) は **仮実装**。AirDroid 再起動 API のリクエストフォーマットは
 * 疎通確認前 (docs/nightly-reboot.md §7 ①) で、成功応答も「要求の経路が成立した」
 * ことしか意味しない。画面の文言もそれが伝わるようにしている。
 *
 * 🚨 **S-9: 疎通確認が完了するまで再起動ボタンは常に無効**
 * ({@link REBOOT_DISABLED_UNTIL_VERIFIED})。dry-run では「成功」が返るのに端末は
 * 再起動しないため、押せる状態のままにすると「偽の成功」を運用者が信じてしまう。
 * 実 API 化のときにフラグを `false` へ戻す。
 */
import { Spinner } from '../components/Spinner';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type React from 'react';

import { ApiError } from '../api/broadcastPoints';
import { deviceTypeFromId, DeviceRegistrationsClient } from '../api/deviceRegistrations';
import type { DeviceRegistration } from '../api/types';
import { useAuth } from '../auth/AuthProvider';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { MasterHistoryLink } from '../components/MasterHistoryLink';
import type { AppConfig } from '../config';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
import { useHistoryWarning } from '../hooks/useHistoryWarning';
import { deviceTarget } from './masterHistoryTarget';
import { describeApiErrorMessage } from '../utils/apiErrorMessage';
import { judgeRemoteValueChange } from '../utils/remoteChange';
import { formatDateTime } from '../utils/dateTime';

interface DeviceRegistrationListPageProps {
  config: AppConfig;
}

/**
 * 現在の列番が解決できない場合の表示。
 *
 * サーバ側 (`GET /device-registrations`) が `current_trip_id: null` を返すのは
 * 「当該編成が在線していない (直近 5 分以内に在線情報が更新されていない)」場合、
 * または VehicleTripMapping の参照に失敗した場合 (フェイルセーフ)。
 */
const CURRENT_TRIP_EMPTY = '—';

/** `current_trip_id` が null のときにツールチップで表示する理由。 */
const CURRENT_TRIP_EMPTY_REASON = '在線情報がありません';

/**
 * 端末名の最大長。サーバ側 (`MAX_DEVICE_NAME_LENGTH` in
 * `lambda/control-panel-api/index.py`) と必ず一致させること。
 * 不一致だと入力できたのに 400 で弾かれるという分かりにくい挙動になる。
 */
const MAX_DEVICE_NAME_LENGTH = 100;

/** デバイスID が未設定のとき、再起動ボタンが押せない理由として示す文言。 */
const AIRDROID_DEVICE_ID_EMPTY_REASON = 'デバイスIDは未設定です';

/**
 * デバイスID の最大長。サーバ側 (`_AIRDROID_DEVICE_ID_PATTERN` in
 * `lambda/control-panel-api/index.py`) の 1〜128 文字と必ず一致させること。
 * 不一致だと入力できたのに 400 で弾かれるという分かりにくい挙動になる。
 */
const MAX_AIRDROID_DEVICE_ID_LENGTH = 128;

/**
 * デバイスID に許可する文字種 (S-5)。
 *
 * サーバ側 `_AIRDROID_DEVICE_ID_PATTERN`
 * (`lambda/control-panel-api/index.py` / `^[A-Za-z0-9._:\-]{1,128}$`) と
 * **必ず同一の正規表現にすること**。`maxLength` では長さしか制限できず、
 * 全角文字やスペース・記号を入力できてしまうため、保存時に 400 で弾かれる
 * (「入力できたのにエラーになる」分かりにくい挙動) のを防ぐ目的で
 * 画面側でも同じ検証を行い、その場でフィードバックする。
 */
const AIRDROID_DEVICE_ID_PATTERN = /^[A-Za-z0-9._:-]{1,128}$/;

/** デバイスID の文字種が不正なときに入力欄の下に出す説明 (S-5)。 */
const AIRDROID_DEVICE_ID_INVALID_REASON =
  'デバイスIDは半角英数字と - _ . : のみ使用できます (1〜128 文字)';

/**
 * サーバが `device_id` 未登録の編成に対して 400 で返すメッセージ (S-5)。
 *
 * `lambda/control-panel-api/index.py` の `DEVICE_ID_NOT_REGISTERED_MESSAGE` と
 * 一致させること。DeviceIdMapping の PK は `device_id` なので、端末が一度も
 * 登録していない編成にはデバイスID を紐づけられない。英語のままでは運用者が
 * 対処を判断できないため、{@link AIRDROID_DEVICE_ID_NOT_REGISTERED_HINT} に
 * 読み替えて表示する。
 */
const SERVER_DEVICE_ID_NOT_REGISTERED_MESSAGE = 'device_id is not registered for this vehicle';

/** 上記 400 を運用者向けに読み替えた文言 (S-5)。 */
const AIRDROID_DEVICE_ID_NOT_REGISTERED_HINT =
  'この編成はまだ端末が登録されていないため、デバイスIDを設定できません (端末が一度アプリに接続すると設定できます)';

/**
 * デバイスID の入力値を検証する (S-5)。
 *
 * 空文字 (前後の空白のみも含む) は「設定の解除」を意味するためエラーにしない
 * (サーバ側も空文字を解除として受け付ける)。
 *
 * @returns 問題があればその理由。問題なければ `null`
 */
function validateAirdroidDeviceId(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  if (!AIRDROID_DEVICE_ID_PATTERN.test(trimmed)) return AIRDROID_DEVICE_ID_INVALID_REASON;
  return null;
}

/**
 * 🚨 再起動ボタンを恒常的に無効化するフラグ (S-9)。
 *
 * AirDroid 再起動 API の**リクエストフォーマットが未確定 (疎通確認前)** のため、
 * RebootWorker は既定で dry-run (`AIRDROID_DRY_RUN=1`) で動作し **HTTP を発行しない**。
 * この状態でボタンを押せてしまうと、`airdroid_client` が `ok=true` /
 * `task_id: "dry-run"` を返すため画面には **「成功」バッジが付くのに端末は再起動しない**。
 * エラーではなく「偽の成功」になるのが問題で、運用者が
 * 「再起動したのに不調が直らない」と誤認する事故につながる。
 *
 * そのため疎通確認が完了するまでは**押せないようにして理由を明示する**。
 *
 * ⚠️ 疎通確認が完了し `AIRDROID_DRY_RUN=0` で実 API 化するときに `false` へ戻す
 * (docs/on-demand-reboot.md §2.3 / §7)。画面の実装 (確認ダイアログ・結果列・
 * 楽観更新) は完成しているので、変更はこの定数 1 行で足りる。
 */
const REBOOT_DISABLED_UNTIL_VERIFIED = true;

/** 疎通確認前のため再起動できないことを示す理由 (S-9)。ボタンの tooltip に出す。 */
const REBOOT_UNVERIFIED_REASON =
  'AirDroid の端末再起動 API の疎通確認が完了していないため、現在は無効です';

/** 最終再起動が一度も無い場合の表示 (C-5)。 */
const LAST_REBOOT_EMPTY = '—';

/** `last_reboot_requested_at` が null のときにツールチップで表示する理由。 */
const LAST_REBOOT_EMPTY_REASON = '再起動の要求履歴がありません';

/**
 * 一括削除で 1 リクエストに指定できる件数の上限。
 * サーバ側 (`MAX_BULK_DELETE_DEVICE_REGISTRATIONS` in `lambda/control-panel-api/index.py`)
 * と必ず一致させること。
 */
const MAX_BULK_DELETE = 100;

/** 一括削除の確認ダイアログ / 実行中の状態 (Issue #638)。 */
interface BulkDeleteState {
  /** 削除対象の編成番号 (確認ダイアログを開いた時点のスナップショット) */
  vehicleIds: string[];
  /** API 呼び出し中フラグ (ConfirmDialog の二重送信防止に使う) */
  requesting: boolean;
}

/**
 * 行ごとの入力中 (未保存) の状態 (Issue #360 / C-5 / Issue #885)。
 *
 * 端末名とデバイスID (`airdroid_device_id`) の入力欄は常に表示している。入力が始まった行だけ
 * この下書きを持ち、保存で常に 2 項目とも送る。サーバの PUT はキーが無い項目を解除するため、
 * 項目ごとに別々に保存するともう片方がクリアされてしまう。
 * 下書きが無い行の入力欄には、一覧の値をそのまま表示する。
 */
interface DeviceRowDraft {
  /** 入力開始時の端末名 (変更有無と他者更新の判定に使う。未設定なら空文字) */
  originalDeviceName: string;
  /** 入力開始時のデバイスID (変更有無と他者更新の判定に使う。未設定なら空文字) */
  originalAirdroidDeviceId: string;
  /** 入力中の端末名 */
  deviceName: string;
  /** 入力中のデバイスID */
  airdroidDeviceId: string;
  /** 保存処理中フラグ (二重送信防止) */
  saving: boolean;
}

/** 下書きが入力開始時から変わっているか (前後の空白は無視する)。 */
function draftHasChanges(draft: DeviceRowDraft): boolean {
  return (
    draft.deviceName.trim() !== draft.originalDeviceName.trim() ||
    draft.airdroidDeviceId.trim() !== draft.originalAirdroidDeviceId.trim()
  );
}

/**
 * 再起動要求の進行状態 (C-5)。
 *
 * `vehicleId` を持つことで「どの行の再起動か」を一意に決め、端末名編集と同じ
 * 「1 行だけ操作可」ロジックを実現する (要求中は他行のボタンを無効化する)。
 */
interface RebootRequestState {
  /** 再起動対象の編成番号 (DeviceRegistry の PK) */
  vehicleId: string;
  /** RebootWorker への要求中フラグ (ConfirmDialog の二重送信防止に使う) */
  requesting: boolean;
}

export function DeviceRegistrationListPage(
  props: DeviceRegistrationListPageProps,
): React.JSX.Element {
  const { config } = props;
  const { getIdToken } = useAuth();

  const apiClient = useMemo(
    () => new DeviceRegistrationsClient({ getIdToken, config }),
    [getIdToken, config],
  );

  /**
   * 編集を破棄するときの確認と、端末名 / デバイスID の更新確定時の他者による更新 / 削除の確認 (Issue #765)。
   * 端末登録は更新日時 (LastUpdate) を持たないため、編集対象の値が開いたときから変わっているかで判定する。
   */
  const { confirmDiscard, guardedUpdate, confirmDialog } = useConfirmDialog();

  // 変更履歴の登録に失敗したときの警告 (履歴はベストエフォートで、操作自体は成功している)
  const { historyWarning, reportHistory } = useHistoryWarning();

  const [items, setItems] = useState<DeviceRegistration[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [abnormalOnly, setAbnormalOnly] = useState(false);
  const requestIdRef = useRef(0);

  // --- 端末名 / デバイスID の行単位の入力 (Issue #360 / C-5 / Issue #885) ---
  // 編成番号 → 下書き。入力が始まった行だけ持つ。
  const [drafts, setDrafts] = useState<Readonly<Record<string, DeviceRowDraft>>>({});

  // --- 再起動要求 (C-5) ---
  // 確認ダイアログの表示対象 (= null なら閉じている) と、要求中フラグを兼ねる。
  const [reboot, setReboot] = useState<RebootRequestState | null>(null);

  // --- 一括削除 (Issue #638) ---
  // チェック済みの編成番号。表示中でない行が混ざっていても、削除時は表示中の行のみに絞る。
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set());
  const [bulkDelete, setBulkDelete] = useState<BulkDeleteState | null>(null);

  /**
   * DeviceRegistry の一覧を取得して state に反映する。
   *
   * `requestIdRef` を用いて古いリクエスト / アンマウント後の結果を破棄し、
   * 競合状態 (race condition) を防ぐ。
   */
  const reload = useCallback(async (): Promise<void> => {
    const currentRequestId = ++requestIdRef.current;
    setLoading(true);
    setErrorMessage(null);
    try {
      const res = await apiClient.list();
      // 古いリクエストまたはアンマウント後は無視
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      setItems(res.items);
      setTotalCount(res.count);
      // 再取得で消えた編成が選択に残らないようにする (Issue #638)
      const existing = new Set(res.items.map((i) => i.vehicle_id));
      setSelectedIds((prev) => {
        const next = new Set([...prev].filter((id) => existing.has(id)));
        return next.size === prev.size ? prev : next;
      });
    } catch (err) {
      // 古いリクエストまたはアンマウント後は無視
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      setErrorMessage(formatApiError(err));
    } finally {
      // アンマウント後や古いリクエストは無視
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [apiClient]);

  // 初回マウント時 (および apiClient の再生成時) に一覧を取得する。
  // React 18 StrictMode 開発環境では effect が 2 回実行されるが、
  // `requestIdRef` による競合防止で最新の結果のみが反映される。
  // cleanup で requestId をインクリメントすることで、進行中の古いリクエスト結果を
  // 全て無視させ、アンマウント後の setState も防ぐ。
  // reload は先頭で setLoading(true) 等の setState を同期実行するため
  // react-hooks/set-state-in-effect が警告するが、これは外部システム (API) から
  // データを取り込む正当なユースケースであり意図的なものなので抑制する。
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- API 取得の初回ロードは正当な副作用
    void reload();

    // アンマウント時に requestId をインクリメントして、
    // 進行中のリクエスト結果を全て無視させ、二重実行の影響も防ぐ。
    // ここでは ref の「現在値を読む」のではなく単にインクリメントするだけなので、
    // exhaustive-deps の「cleanup 時に ref が変わっている可能性」警告は該当しない。
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps -- ref はカウンタ用途で、cleanup での加算は意図的
      ++requestIdRef.current;
    };
  }, [reload]);

  const visibleItems = useMemo(
    () => (abnormalOnly ? items.filter((i) => i.status === 'abnormal') : items),
    [items, abnormalOnly],
  );

  /** 表示中かつチェック済みの編成番号 (一括削除の対象)。 */
  const selectedVisibleIds = useMemo(
    () => visibleItems.filter((i) => selectedIds.has(i.vehicle_id)).map((i) => i.vehicle_id),
    [visibleItems, selectedIds],
  );
  const allVisibleSelected =
    visibleItems.length > 0 && selectedVisibleIds.length === visibleItems.length;

  /** 1 行のチェックを切り替える (Issue #638)。 */
  const toggleSelected = useCallback((vehicleId: string, checked: boolean): void => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(vehicleId);
      } else {
        next.delete(vehicleId);
      }
      return next;
    });
  }, []);

  /** 表示中の行を全選択 / 全解除する (Issue #638)。 */
  const toggleAllVisible = useCallback(
    (checked: boolean): void => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const item of visibleItems) {
          if (checked) {
            next.add(item.vehicle_id);
          } else {
            next.delete(item.vehicle_id);
          }
        }
        return next;
      });
    },
    [visibleItems],
  );

  /** 一括削除の確認ダイアログを開く (まだ API は呼ばない)。 */
  const startBulkDelete = useCallback((): void => {
    if (selectedVisibleIds.length === 0) return;
    if (selectedVisibleIds.length > MAX_BULK_DELETE) {
      setErrorMessage(
        `一度に削除できるのは ${MAX_BULK_DELETE} 件までです (選択中: ${selectedVisibleIds.length} 件)`,
      );
      return;
    }
    setErrorMessage(null);
    setBulkDelete({ vehicleIds: selectedVisibleIds, requesting: false });
  }, [selectedVisibleIds]);

  /** 一括削除の確認ダイアログを閉じる (削除は行わない)。 */
  const cancelBulkDelete = useCallback((): void => {
    setBulkDelete(null);
  }, []);

  /**
   * 一括削除を実行する (Issue #638)。
   *
   * 成功した編成は選択から外し、一覧を再取得して表示を揃える。サーバは
   * 部分成功 (`failed` あり) を 200 で返すため、失敗分は画面にメッセージで示し、
   * 失敗した行のチェックは残して再試行できるようにする。
   */
  const confirmBulkDelete = useCallback(async (): Promise<void> => {
    if (bulkDelete === null || bulkDelete.requesting) return;
    const target = bulkDelete;
    setBulkDelete({ ...target, requesting: true });
    setErrorMessage(null);
    try {
      const result = await apiClient.bulkDelete(target.vehicleIds);
      reportHistory(result);
      const failed = new Set(result.failed);
      setSelectedIds((prev) => new Set([...prev].filter((id) => failed.has(id))));
      setBulkDelete(null);
      await reload();
      if (result.failed.length > 0) {
        // reload は先頭でエラーをクリアするため、その後に失敗を表示する
        setErrorMessage(
          `${result.failed.length} 件の削除に失敗しました: ${result.failed.join(', ')}`,
        );
      }
    } catch (err) {
      setErrorMessage(formatApiError(err));
      setBulkDelete(null);
    }
  }, [apiClient, bulkDelete, reload, reportHistory]);

  /** 行の下書きを返す。入力が始まっていなければ、一覧の値から作る (Issue #885)。 */
  const draftOf = useCallback(
    (item: DeviceRegistration): DeviceRowDraft =>
      drafts[item.vehicle_id] ?? {
        originalDeviceName: item.device_name ?? '',
        originalAirdroidDeviceId: item.airdroid_device_id ?? '',
        deviceName: item.device_name ?? '',
        airdroidDeviceId: item.airdroid_device_id ?? '',
        saving: false,
      },
    [drafts],
  );

  /**
   * 入力欄の変更を下書きに反映する (Issue #885)。
   * 入力が始まった時点の一覧の値を「元の値」として持ち、変更有無と他者更新の判定に使う。
   */
  const updateDraft = useCallback(
    (
      item: DeviceRegistration,
      patch: Partial<Pick<DeviceRowDraft, 'deviceName' | 'airdroidDeviceId'>>,
    ): void => {
      setDrafts((prev) => {
        const current = prev[item.vehicle_id] ?? {
          originalDeviceName: item.device_name ?? '',
          originalAirdroidDeviceId: item.airdroid_device_id ?? '',
          deviceName: item.device_name ?? '',
          airdroidDeviceId: item.airdroid_device_id ?? '',
          saving: false,
        };
        return { ...prev, [item.vehicle_id]: { ...current, ...patch } };
      });
    },
    [],
  );

  /** 行の下書きを捨てる (入力欄は一覧の値に戻る)。 */
  const discardDraft = useCallback((vehicleId: string): void => {
    setDrafts((prev) => {
      if (!(vehicleId in prev)) return prev;
      const next = { ...prev };
      delete next[vehicleId];
      return next;
    });
  }, []);

  /**
   * 行の入力を保存する (Issue #885)。
   *
   *   - 両項目を trim し、デバイスID はサーバと同じ文字種で検証する (S-5)
   *   - どちらも変更が無ければ API を呼ばずに下書きを捨てる
   *   - 常に 2 項目とも送る (片方だけ送るとサーバ側でもう片方が解除される)
   *   - 成功時は該当行だけをサーバ応答で差し替える。全件 reload しないのは
   *     「異常のみ表示」フィルタやスクロール位置を保ったまま更新するため
   *   - 失敗時は入力を保持したまま再試行できるようにする
   */
  const saveRow = useCallback(
    async (vehicleId: string): Promise<void> => {
      const target = drafts[vehicleId];
      if (target === undefined || target.saving) return;
      const deviceName = target.deviceName.trim();
      const airdroidDeviceId = target.airdroidDeviceId.trim();
      // 保存ボタンは不正な間無効化しているが、Enter キーからも保存できるので
      // ここでも弾いて 400 になる無駄な往復を防ぐ。
      const invalidReason = validateAirdroidDeviceId(airdroidDeviceId);
      if (invalidReason !== null) {
        setErrorMessage(invalidReason);
        return;
      }
      if (!draftHasChanges(target)) {
        discardDraft(vehicleId);
        return;
      }
      setDrafts((prev) => ({ ...prev, [vehicleId]: { ...target, saving: true } }));
      setErrorMessage(null);
      try {
        const updated = await apiClient.updateDevice(vehicleId, {
          deviceName,
          airdroidDeviceId,
        });
        reportHistory(updated);
        setItems((prev) =>
          prev.map((item) =>
            item.vehicle_id === vehicleId
              ? // サーバ応答には current_trip_id が含まれない (一覧取得専用の合成値) ため、
                // 表示中の値を保持する。airdroid_device_id は PUT 応答に合成値として
                // 必ず含まれるので、応答をそのまま反映する (S-2)。
                { ...item, ...updated, current_trip_id: item.current_trip_id }
              : item,
          ),
        );
        discardDraft(vehicleId);
      } catch (err) {
        setErrorMessage(formatAirdroidSaveError(err));
        setDrafts((prev) =>
          prev[vehicleId] ? { ...prev, [vehicleId]: { ...prev[vehicleId], saving: false } } : prev,
        );
      }
    },
    [apiClient, discardDraft, drafts, reportHistory],
  );

  /**
   * 保存ボタン / Enter の入口 (Issue #765 / #885)。
   * 変更があり、かつデバイスID の文字種が正しいときだけ、保存前に他者の更新 / 削除を確認する。
   * 変更無し / 不正な入力は {@link saveRow} が API を呼ばずに閉じる / エラーを出すので確認しない。
   */
  const requestSaveRow = useCallback(
    (vehicleId: string): void => {
      const target = drafts[vehicleId];
      if (target === undefined || target.saving) return;
      const invalid = validateAirdroidDeviceId(target.airdroidDeviceId) !== null;
      void guardedUpdate({
        hasChanges: !invalid && draftHasChanges(target),
        fetchRemoteState: async () => {
          const res = await apiClient.list();
          const current = res.items.find((item) => item.vehicle_id === vehicleId);
          // 2 項目のどちらかが入力を始めた時点から変わっていれば「他者が更新した」とする。
          const nameJudge = judgeRemoteValueChange(
            target.originalDeviceName,
            current ? { value: current.device_name } : null,
          );
          if (nameJudge !== 'unchanged') return nameJudge;
          return judgeRemoteValueChange(
            target.originalAirdroidDeviceId,
            current ? { value: current.airdroid_device_id } : null,
          );
        },
        execute: () => void saveRow(vehicleId),
        // 他者に削除されていたら保存できない。お知らせを閉じたら下書きを捨てて一覧を取り直す。
        onDeleted: () => {
          discardDraft(vehicleId);
          void reload();
        },
      });
    },
    [apiClient, discardDraft, drafts, guardedUpdate, reload, saveRow],
  );

  /** Escape の入口。入力を変えていれば破棄の確認を挟み、一覧の値に戻す (Issue #765)。 */
  const requestDiscardDraft = useCallback(
    (vehicleId: string): void => {
      const target = drafts[vehicleId];
      if (target === undefined || target.saving) return;
      if (draftHasChanges(target)) {
        confirmDiscard(() => discardDraft(vehicleId));
        return;
      }
      discardDraft(vehicleId);
    },
    [confirmDiscard, discardDraft, drafts],
  );

  /** 入力欄のキー操作。Enter で行保存 / Escape で入力を破棄 (表形式の編集で一般的な操作)。 */
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>, vehicleId: string): void => {
      if (e.key === 'Enter') {
        e.preventDefault();
        requestSaveRow(vehicleId);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        requestDiscardDraft(vehicleId);
      }
    },
    [requestDiscardDraft, requestSaveRow],
  );

  /** 再起動の確認ダイアログを開く (C-5)。まだ API は呼ばない。 */
  const startReboot = useCallback((item: DeviceRegistration): void => {
    setErrorMessage(null);
    setReboot({ vehicleId: item.vehicle_id, requesting: false });
  }, []);

  /** 再起動の確認ダイアログを閉じる (要求は行わない)。 */
  const cancelReboot = useCallback((): void => {
    setReboot(null);
  }, []);

  /**
   * 再起動を要求する (C-5)。
   *
   * 成功したら該当行の再起動結果 3 属性を **その場で更新** する。サーバの応答は
   * RebootWorker の戻り値 (要求時刻を含まない) なので、`last_reboot_requested_at`
   * はクライアント時刻ではなく **再取得** した方が正確だが、一覧全体の再取得は
   * フィルタやスクロール位置を巻き戻すため、要求直後の暫定表示として
   * 応答の値 (`ok` / `task_id`) を反映し、正確な時刻は次回の再読み込みで揃える。
   */
  const confirmReboot = useCallback(async (): Promise<void> => {
    if (reboot === null || reboot.requesting) return;
    const target = reboot;
    setReboot({ ...target, requesting: true });
    setErrorMessage(null);
    try {
      const result = await apiClient.rebootDevice(target.vehicleId);
      reportHistory(result);
      setItems((prev) =>
        prev.map((item) =>
          item.vehicle_id === target.vehicleId
            ? {
                ...item,
                last_reboot_result: result.ok ? 'success' : 'failed',
                last_reboot_task_id: result.task_id,
              }
            : item,
        ),
      );
      setReboot(null);
      // 要求時刻 (last_reboot_requested_at) はサーバが記録した値が正なので、
      // 一覧を再取得して表示を揃える。失敗しても再起動要求自体は成立している
      // ため、ここでは reload のエラーをそのまま画面に出すだけに留める。
      await reload();
    } catch (err) {
      // 失敗時はダイアログを閉じ、エラーメッセージで理由を示す
      // (409 = デバイスID 未登録 / 502 = RebootWorker 失敗 など)。
      setErrorMessage(formatApiError(err));
      setReboot(null);
    }
  }, [apiClient, reboot, reload, reportHistory]);

  /**
   * 行の操作 (再起動 / 選択 / 保存) を無効化すべきか (C-5)。
   *
   * 再起動の確認中・一括削除の確認中・いずれかの行の保存中は、競合更新と誤操作を防ぐため
   * 他の操作を無効化する。入力中 (未保存) の行があるだけでは無効化しない。
   */
  const isRowLocked =
    Object.values(drafts).some((d) => d.saving) || reboot !== null || bulkDelete !== null;

  return (
    <section className="section section--page list-page" data-testid="device-list-section">
      <h2>
        端末登録一覧{' '}
        {loading ? (
          <>
            <Spinner />
            (読み込み中...)
          </>
        ) : visibleItems.length === items.length ? (
          `(${totalCount} 件)`
        ) : (
          `(${visibleItems.length} / ${totalCount} 件)`
        )}
      </h2>

      {errorMessage ? <div className="error-message">{errorMessage}</div> : null}

      {historyWarning}

      <div className="sort-filter-bar">
        <label className="sort-filter-radio">
          <input
            type="checkbox"
            checked={abnormalOnly}
            onChange={(e) => setAbnormalOnly(e.target.checked)}
            data-testid="abnormal-only-toggle"
          />
          異常のみ表示
        </label>
        <button type="button" onClick={() => void reload()} data-testid="device-reload-button">
          再読み込み
        </button>
        {/* Issue #638: チェックした端末の一括削除。未選択の間は押せない。 */}
        <button
          type="button"
          className="danger"
          onClick={startBulkDelete}
          disabled={selectedVisibleIds.length === 0 || isRowLocked || loading}
          data-testid="device-bulk-delete-button"
        >
          選択した端末を削除 ({selectedVisibleIds.length})
        </button>
      </div>

      {/* 表だけをスクロールさせ、タイトル・操作バー・表の見出し行は固定する (Issue #765) */}
      <div className="list-page__body" data-testid="device-list-body">
        <div className="table-wrapper">
          <table className="data-table" data-testid="device-table">
            <thead>
              <tr>
                {/* Issue #638: 一括削除用。ヘッダーのチェックで表示中の行を全選択 / 全解除する。 */}
                <th scope="col">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={(e) => toggleAllVisible(e.target.checked)}
                    disabled={visibleItems.length === 0 || isRowLocked}
                    aria-label="表示中の端末をすべて選択"
                    data-testid="device-select-all"
                  />
                </th>
                <th scope="col">編成番号</th>
                <th scope="col">端末ID</th>
                {/* Issue #360: 端末名 (任意入力)。行ごとに鉛筆アイコンからインライン編集できる。 */}
                <th scope="col">端末名</th>
                {/*
                  C-5: デバイスID (airdroid_device_id)。AirDroid Business 上の端末 ID を
                  運用者が転記する。端末名と同じ鉛筆アイコンからインライン編集できる。
                */}
                <th scope="col">デバイスID</th>
                <th scope="col">種別</th>
                <th scope="col">最終登録日時</th>
                <th scope="col">現在の列番</th>
                <th scope="col">ステータス</th>
                <th scope="col">状態更新時刻</th>
                {/* C-5: 再起動の要求時刻 + 結果バッジ */}
                <th scope="col">最終再起動</th>
                {/* C-5: 行ごとの再起動ボタン */}
                <th scope="col">操作</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.length === 0 ? (
                <tr>
                  {/*
                    列数と colSpan がずれると空表示のレイアウトが崩れるため、
                    列を増やしたらここも必ず合わせること (C-5 で 8 → 11、#638 で 11 → 12)。
                  */}
                  <td colSpan={12} className="data-table__empty">
                    {loading ? (
                      <>
                        <Spinner />
                        読み込み中...
                      </>
                    ) : (
                      '該当する端末がありません'
                    )}
                  </td>
                </tr>
              ) : (
                visibleItems.map((item) => {
                  const draft = draftOf(item);
                  // C-5: AirDroid 端末 ID が未登録の端末は再起動 API が 409 を返すため、
                  // ボタン自体を押せないようにして理由を title / aria-label で示す。
                  const canReboot = Boolean(item.airdroid_device_id);
                  // S-9: 再起動ボタンを押せない理由 (押せるなら null)。
                  //   疎通確認前は **デバイスID の有無を問わず** 無効にする。dry-run では
                  //   「成功」が返るのに端末は再起動しないため、押せること自体が誤解を生む。
                  const rebootDisabledReason = REBOOT_DISABLED_UNTIL_VERIFIED
                    ? REBOOT_UNVERIFIED_REASON
                    : canReboot
                      ? null
                      : `再起動できません: ${AIRDROID_DEVICE_ID_EMPTY_REASON}`;
                  // S-5: 入力中のデバイスID がサーバの文字種 (`_AIRDROID_DEVICE_ID_PATTERN`)
                  // に反していれば、その理由を保存前に表示して保存ボタンを無効化する。
                  const airdroidInvalidReason = validateAirdroidDeviceId(draft.airdroidDeviceId);
                  // 保存ボタンは変更が無い間も押せない (無駄な API 呼び出しを避ける)。
                  const rowHasChanges = draftHasChanges(draft);
                  return (
                    <tr key={item.vehicle_id} data-testid={`device-row-${item.vehicle_id}`}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.vehicle_id)}
                          onChange={(e) => toggleSelected(item.vehicle_id, e.target.checked)}
                          disabled={isRowLocked}
                          aria-label={`${item.vehicle_id} を選択`}
                          data-testid={`device-select-${item.vehicle_id}`}
                        />
                      </td>
                      <td>{item.vehicle_id}</td>
                      <td>{item.device_id}</td>
                      {/*
                        Issue #360 / #885: 端末名 (任意入力)。入力欄を常に表示する。
                        保存はデバイスID の横の「保存」で、端末名とデバイスID を 2 項目とも送る。
                      */}
                      <td>
                        <div className="device-name-cell">
                          <input
                            type="text"
                            className="device-name-cell__input"
                            value={draft.deviceName}
                            maxLength={MAX_DEVICE_NAME_LENGTH}
                            onChange={(e) => updateDraft(item, { deviceName: e.target.value })}
                            onKeyDown={(e) => handleInputKeyDown(e, item.vehicle_id)}
                            disabled={draft.saving}
                            aria-label={`${item.vehicle_id} の端末名`}
                            placeholder="端末名 (任意)"
                            data-testid={`device-name-input-${item.vehicle_id}`}
                          />
                        </div>
                      </td>
                      {/*
                        C-5: デバイスID (airdroid_device_id / AirDroid Business 上の端末 ID)。
                        入力欄の横に行単位の「保存」ボタンを置く (Issue #885)。キャンセルボタンは無い。
                      */}
                      <td>
                        <div className="device-name-cell">
                          <input
                            type="text"
                            className="device-name-cell__input"
                            value={draft.airdroidDeviceId}
                            maxLength={MAX_AIRDROID_DEVICE_ID_LENGTH}
                            // 文字種エラーは読み上げにも伝える必要があるため、
                            // aria-invalid と説明テキストの関連付けを行う (S-5)。
                            aria-invalid={airdroidInvalidReason !== null}
                            aria-describedby={
                              airdroidInvalidReason !== null
                                ? `device-airdroid-id-error-${item.vehicle_id}`
                                : undefined
                            }
                            onChange={(e) =>
                              updateDraft(item, { airdroidDeviceId: e.target.value })
                            }
                            onKeyDown={(e) => handleInputKeyDown(e, item.vehicle_id)}
                            disabled={draft.saving}
                            aria-label={`${item.vehicle_id} のデバイスID`}
                            placeholder="デバイスID (任意)"
                            data-testid={`device-airdroid-id-input-${item.vehicle_id}`}
                          />
                          <button
                            type="button"
                            className="primary"
                            onClick={() => requestSaveRow(item.vehicle_id)}
                            // S-5: 文字種が不正 / 変更無し / どの行でも保存中・確認中は押せない
                            disabled={
                              draft.saving ||
                              airdroidInvalidReason !== null ||
                              !rowHasChanges ||
                              isRowLocked
                            }
                            title="端末名とデバイスID を保存"
                            aria-label={`${item.vehicle_id} の端末名とデバイスID を保存`}
                            data-testid={`device-save-${item.vehicle_id}`}
                          >
                            {draft.saving ? <Spinner /> : null}
                            保存
                          </button>
                        </div>
                        {/*
                          S-5: 文字種エラーの即時フィードバック。`maxLength` では長さしか
                          制限できず、全角や記号を入力できてしまうため、保存前にセル内で
                          理由を示す (サーバへ送って 400 を受けるより分かりやすい)。
                        */}
                        {airdroidInvalidReason !== null && (
                          <p
                            className="device-name-cell__error"
                            id={`device-airdroid-id-error-${item.vehicle_id}`}
                            data-testid={`device-airdroid-id-error-${item.vehicle_id}`}
                          >
                            {airdroidInvalidReason}
                          </p>
                        )}
                      </td>
                      <td>{deviceTypeFromId(item.device_id)}</td>

                      <td>{formatDateTime(item.last_registered_at)}</td>
                      <td>
                        {item.current_trip_id ? (
                          <span data-testid="current-trip-id">{item.current_trip_id}</span>
                        ) : (
                          <span title={CURRENT_TRIP_EMPTY_REASON} data-testid="current-trip-empty">
                            {CURRENT_TRIP_EMPTY}
                            {/*
                            記号 "—" だけでは読み上げで理由が伝わらないため、
                            スクリーンリーダー専用テキストで補う (視覚上は非表示)。
                            マウス利用者には title 属性のツールチップで同じ情報を提供する。
                          */}
                            <span className="visually-hidden">（{CURRENT_TRIP_EMPTY_REASON}）</span>
                          </span>
                        )}
                      </td>
                      <td>
                        <StatusBadge
                          status={item.status}
                          lastAlertSentAt={item.last_alert_sent_at}
                        />
                      </td>
                      <td>{formatDateTime(item.status_updated_at)}</td>
                      {/*
                        C-5: 最終再起動。要求時刻 (last_reboot_requested_at) と
                        結果バッジ (last_reboot_result) を並べる。
                      */}
                      <td>
                        <div className="device-name-cell">
                          {item.last_reboot_requested_at ? (
                            <span data-testid={`device-last-reboot-${item.vehicle_id}`}>
                              {formatDateTime(item.last_reboot_requested_at)}
                            </span>
                          ) : (
                            <span
                              title={LAST_REBOOT_EMPTY_REASON}
                              data-testid={`device-last-reboot-empty-${item.vehicle_id}`}
                            >
                              {LAST_REBOOT_EMPTY}
                              {/* 記号だけでは読み上げで理由が伝わらないため補助テキストを添える */}
                              <span className="visually-hidden">
                                （{LAST_REBOOT_EMPTY_REASON}）
                              </span>
                            </span>
                          )}
                          <RebootResultBadge
                            result={item.last_reboot_result}
                            taskId={item.last_reboot_task_id}
                          />
                        </div>
                      </td>
                      {/* C-5: 操作 (再起動ボタン)。押下すると ConfirmDialog で確認する。 */}
                      <td>
                        <button
                          type="button"
                          className="danger"
                          onClick={() => startReboot(item)}
                          // S-9: 疎通確認前は常に無効 / AirDroid 端末 ID 未登録 (409 になる) /
                          // 他操作進行中も押せない。理由は title で示す (下の rebootDisabledReason)。
                          disabled={rebootDisabledReason !== null || isRowLocked}
                          title={rebootDisabledReason ?? 'この編成の端末を再起動する'}
                          // S-9: 無効の理由は title (ホバー) だけでなく aria-label にも載せる。
                          // disabled なボタンは title を読み上げないスクリーンリーダーがあり、
                          // 「押せないが理由が分からない」状態を避けるため
                          // (結果バッジが `再起動結果: 成功。${tooltip}` としているのと同じ流儀)。
                          aria-label={
                            rebootDisabledReason === null
                              ? `${item.vehicle_id} の端末を再起動`
                              : `${item.vehicle_id} の端末を再起動。${rebootDisabledReason}`
                          }
                          data-testid={`device-reboot-${item.vehicle_id}`}
                        >
                          再起動
                        </button>
                        <MasterHistoryLink
                          target={deviceTarget(item.vehicle_id)}
                          subject={item.vehicle_id}
                          testId={`device-history-link-${item.vehicle_id}`}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/*
        C-5: 再起動の確認ダイアログ。window.confirm ではなく共通の ConfirmDialog を
        使い、destructive (赤) + confirmDisabled / cancelDisabled で
        要求中の二重送信・誤操作を防ぐ (AlertRecipientPage / BroadcastPointsPage と同じ)。
      */}
      {confirmDialog}

      <ConfirmDialog
        open={reboot !== null}
        title="端末の再起動"
        message={
          reboot
            ? `編成 ${reboot.vehicleId} の端末を再起動します。\n` +
              '放送中の場合は中断されます。よろしいですか？\n' +
              '※ 再起動 API は疎通確認前の仮実装で、成功しても実際に再起動される保証はありません。'
            : ''
        }
        confirmLabel="再起動"
        destructive
        confirmDisabled={reboot?.requesting ?? false}
        cancelDisabled={reboot?.requesting ?? false}
        onConfirm={() => void confirmReboot()}
        onCancel={cancelReboot}
      />

      {/* Issue #638: 一括削除の確認ダイアログ (再起動と同じ ConfirmDialog / destructive)。 */}
      <ConfirmDialog
        open={bulkDelete !== null}
        title="端末の一括削除"
        message={
          bulkDelete
            ? `選択した ${bulkDelete.vehicleIds.length} 件の端末登録を削除します。\n` +
              `対象: ${bulkDelete.vehicleIds.join(', ')}\n` +
              'この操作は元に戻せません。よろしいですか？'
            : ''
        }
        confirmLabel="削除"
        destructive
        confirmDisabled={bulkDelete?.requesting ?? false}
        cancelDisabled={bulkDelete?.requesting ?? false}
        onConfirm={() => void confirmBulkDelete()}
        onCancel={cancelBulkDelete}
      />
    </section>
  );
}

interface RebootResultBadgeProps {
  result: DeviceRegistration['last_reboot_result'];
  taskId: string | null;
}

/**
 * 再起動結果バッジ (C-5)。
 *
 * {@link StatusBadge} と同じ 3 状態パターンで表示する:
 * - `success`: 緑バッジ「成功」
 * - `failed`: 赤バッジ「失敗」
 * - null / その他: 中立バッジ「未実行」
 *
 * `title` には AirDroid 側のタスク ID (`last_reboot_task_id`) を出し、障害調査で
 * AirDroid 管理コンソールと突き合わせられるようにする (取得できなかった場合は
 * 空文字が記録されるため「タスク ID なし」と表示する)。
 */
function RebootResultBadge({ result, taskId }: RebootResultBadgeProps): React.JSX.Element {
  if (result === null || result === undefined) {
    return (
      <span
        className="status-badge status-badge--unknown"
        aria-label="再起動結果: 未実行"
        data-testid="reboot-result-badge"
      >
        未実行
      </span>
    );
  }
  const tooltip = taskId ? `タスク ID: ${taskId}` : 'タスク ID なし';
  if (result === 'success') {
    return (
      <span
        className="status-badge status-badge--normal"
        title={tooltip}
        aria-label={`再起動結果: 成功。${tooltip}`}
        tabIndex={0}
        data-testid="reboot-result-badge"
      >
        成功
      </span>
    );
  }
  return (
    <span
      className="status-badge status-badge--abnormal"
      title={tooltip}
      aria-label={`再起動結果: 失敗。${tooltip}`}
      tabIndex={0}
      data-testid="reboot-result-badge"
    >
      失敗
    </span>
  );
}

interface StatusBadgeProps {
  status: DeviceRegistration['status'];
  lastAlertSentAt: string | null;
}

/**
 * ステータスバッジ。
 * - `normal`: 緑バッジ「正常」
 * - `abnormal`: 赤バッジ「異常」+ last_alert_sent_at をツールチップ (title) 表示
 * - その他 / null: 中立バッジ「不明」
 */
function StatusBadge({ status, lastAlertSentAt }: StatusBadgeProps): React.JSX.Element {
  if (status === 'normal') {
    return (
      <span
        className="status-badge status-badge--normal"
        aria-label="ステータス: 正常"
        data-testid="status-badge"
      >
        正常
      </span>
    );
  }
  if (status === 'abnormal') {
    const tooltip = lastAlertSentAt
      ? `最終アラート送信: ${formatDateTime(lastAlertSentAt)}`
      : 'アラート送信履歴なし';
    return (
      <span
        className="status-badge status-badge--abnormal"
        title={tooltip}
        aria-label={`ステータス: 異常。${tooltip}`}
        tabIndex={0}
        data-testid="status-badge"
      >
        異常
      </span>
    );
  }
  return (
    <span
      className="status-badge status-badge--unknown"
      aria-label="ステータス: 不明"
      data-testid="status-badge"
    >
      不明
    </span>
  );
}

/**
 * デバイスID 保存時の例外を画面表示用文字列に整形する (S-5)。
 *
 * S-2 で追加された 400 (`device_id` 未登録の編成にデバイスID を設定しようとした)
 * はサーバのメッセージが英語のため、運用者が「何をすれば直るのか」を判断できない。
 * 該当する場合だけ日本語の手引きに読み替え、それ以外は {@link formatApiError} と
 * 同じ日本語メッセージで表示する。
 */
function formatAirdroidSaveError(err: unknown): string {
  if (
    err instanceof ApiError &&
    err.status === 400 &&
    err.message === SERVER_DEVICE_ID_NOT_REGISTERED_MESSAGE
  ) {
    return AIRDROID_DEVICE_ID_NOT_REGISTERED_HINT;
  }
  return formatApiError(err);
}

/** API クライアントの例外を画面表示用の日本語メッセージに整形する (Issue #815) */
function formatApiError(err: unknown): string {
  return describeApiErrorMessage(err);
}
